import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { GraduationCap, Plus } from 'lucide-react';
import { AppError } from '@/core/errors/AppError';
import { useSession } from '@/core/auth/useSession';
import { useDesactivarEmpleado, useEmpleadosLista } from '@/data/hooks/useEmpleados';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { formatearFecha } from '@/core/utils/formatters';
import { Button } from '@/shared/components/Button';
import { Chip } from '@/shared/components/Chip';
import { ConfirmDialog } from '@/shared/components/ConfirmDialog';
import { Pagination } from '@/shared/components/Pagination';
import { SearchInput } from '@/shared/components/SearchInput';
import { Select } from '@/shared/components/Select';
import { Table } from '@/shared/components/Table';
import { useToast } from '@/shared/components/Toast';
import { EmpleadoFormDialog } from './EmpleadoFormDialog';
import { EmpleadoDetalleDialog } from './EmpleadoDetalleDialog';
const POR_PAGINA = 20;
export function EmpleadosPage() {
    const { tieneRol } = useSession();
    const puedeAdmin = tieneRol('ROLE_ADMINISTRAR_EMPLEADO');
    const toast = useToast();
    const [busq, setBusq] = useState('');
    const [estado, setEstado] = useState('activos');
    const [pagina, setPagina] = useState(1);
    const q = useDebounce(busq, 300);
    const { data, isLoading, isError, error, refetch } = useEmpleadosLista({
        q,
        activo: estado === 'todos' ? undefined : estado === 'activos',
        pagina,
        porPagina: POR_PAGINA,
    });
    const [formOpen, setFormOpen] = useState(false);
    const [editable, setEditable] = useState(null);
    const [detalleOpen, setDetalleOpen] = useState(false);
    const [seleccionado, setSeleccionado] = useState(null);
    const [desactivando, setDesactivando] = useState(null);
    const desactivar = useDesactivarEmpleado();
    const columnas = useMemo(() => [
        {
            key: 'nombre',
            header: 'Instructor',
            cell: (e) => (_jsxs("div", { className: "flex flex-col", children: [_jsxs("span", { className: "font-medium", children: [e.nombre, " ", e.apellido] }), _jsxs("span", { className: "text-xs text-text-tertiary", children: [e.tipoDocumento, " ", e.nroDocumento] })] })),
        },
        {
            key: 'contacto',
            header: 'Contacto',
            cell: (e) => (_jsxs("div", { className: "flex flex-col text-xs", children: [_jsx("span", { children: e.email ?? '—' }), _jsx("span", { className: "text-text-tertiary", children: e.telefono ?? '—' })] })),
            width: '220px',
        },
        {
            key: 'tipo',
            header: 'Tipo',
            cell: (e) => _jsx(Chip, { tono: "primary", children: e.tipo }),
            width: '120px',
        },
        {
            key: 'alta',
            header: 'Alta',
            cell: (e) => formatearFecha(e.fechaAlta),
            width: '120px',
        },
        {
            key: 'estado',
            header: 'Estado',
            width: '110px',
            cell: (e) => e.activo ? _jsx(Chip, { tono: "success", children: "Activo" }) : _jsx(Chip, { tono: "neutral", children: "Inactivo" }),
        },
    ], []);
    const abrirNuevo = () => {
        setEditable(null);
        setFormOpen(true);
    };
    const abrirEdicion = (e) => {
        setEditable(e);
        setDetalleOpen(false);
        setFormOpen(true);
    };
    const ejecutarDesactivar = (e) => {
        desactivar.mutate(e.id, {
            onSuccess: () => {
                toast.exito('Instructor desactivado');
                setDesactivando(null);
                setDetalleOpen(false);
            },
            onError: (err) => toast.error(err instanceof AppError ? err.mensaje : 'No pudimos desactivarlo.'),
        });
    };
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("div", { className: "flex flex-wrap items-start justify-between gap-3", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-xl font-bold text-text-primary", children: "Instructores" }), _jsx("p", { className: "text-xs text-text-secondary", children: "Gestion\u00E1 los instructores, sus datos y las disciplinas habilitadas." })] }), puedeAdmin ? (_jsxs(Button, { onClick: abrirNuevo, children: [_jsx(Plus, { className: "h-4 w-4" }), "Nuevo instructor"] })) : null] }), _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(SearchInput, { value: busq, onChange: (v) => {
                            setBusq(v);
                            setPagina(1);
                        }, placeholder: "Buscar por nombre, email o documento\u2026" }), _jsxs(Select, { value: estado, onChange: (e) => {
                            setEstado(e.target.value);
                            setPagina(1);
                        }, className: "w-36", children: [_jsx("option", { value: "activos", children: "Activos" }), _jsx("option", { value: "inactivos", children: "Inactivos" }), _jsx("option", { value: "todos", children: "Todos" })] })] }), isError ? (_jsxs("div", { className: "rounded-lg border border-danger bg-danger-fill px-3 py-2 text-sm text-danger", children: [error instanceof AppError ? error.mensaje : 'Error al cargar.', ' ', _jsx("button", { onClick: () => refetch(), className: "ml-2 underline", children: "Reintentar" })] })) : (_jsxs(_Fragment, { children: [_jsx(Table, { columns: columnas, items: data?.items ?? [], rowKey: (e) => e.id, onRowClick: (e) => {
                            setSeleccionado(e);
                            setDetalleOpen(true);
                        }, cargando: isLoading, vacioTitulo: "Sin instructores", vacioDescripcion: q ? 'Probá con otra búsqueda.' : 'Creá el primer instructor.', vacioAccion: puedeAdmin && !q ? (_jsxs(Button, { onClick: abrirNuevo, children: [_jsx(GraduationCap, { className: "h-4 w-4" }), "Crear el primero"] })) : null }), data ? (_jsx(Pagination, { pagina: data.paginacion.pagina, totalPaginas: data.paginacion.totalPaginas, total: data.paginacion.total, porPagina: data.paginacion.porPagina, onCambioPagina: setPagina })) : null] })), _jsx(EmpleadoFormDialog, { open: formOpen, onOpenChange: setFormOpen, empleado: editable }), _jsx(EmpleadoDetalleDialog, { open: detalleOpen, onOpenChange: setDetalleOpen, empleado: seleccionado, onEditar: () => seleccionado && abrirEdicion(seleccionado), onDesactivar: () => seleccionado && setDesactivando(seleccionado) }), _jsx(ConfirmDialog, { open: Boolean(desactivando), onOpenChange: (open) => !open && setDesactivando(null), titulo: "Desactivar instructor", descripcion: desactivando
                    ? `${desactivando.nombre} ${desactivando.apellido} dejará de aparecer en el timeline y los listados activos.`
                    : undefined, textoConfirmar: "Desactivar", destructivo: true, cargando: desactivar.isPending, onConfirmar: () => desactivando && ejecutarDesactivar(desactivando) })] }));
}
